import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import client from "../api/client";

const MetaContext = createContext(null);

const EMPTY_ASSESSMENT_TEMPLATE = {
  stages: [],
  opening_guidance: [],
  main_objectives: []
};

export function MetaProvider({ children }) {
  const [assessmentTemplate, setAssessmentTemplate] = useState(EMPTY_ASSESSMENT_TEMPLATE);
  const [toolCatalog, setToolCatalog] = useState([]);
  const [toolIndustryGroups, setToolIndustryGroups] = useState([]);
  const [metaLoading, setMetaLoading] = useState(true);
  const [metaError, setMetaError] = useState("");

  useEffect(() => {
    let cancelled = false;

    const loadMeta = async () => {
      setMetaLoading(true);
      try {
        const [assessmentRes, toolsRes] = await Promise.all([
          client.get("/public/meta/assessment-template", { skipAuthRedirect: true }),
          client.get("/public/meta/tools-catalog", { skipAuthRedirect: true })
        ]);

        if (cancelled) return;

        const nextAssessment = assessmentRes?.data || EMPTY_ASSESSMENT_TEMPLATE;
        const nextCatalog = toolsRes?.data?.catalog || [];
        const nextIndustryGroups = toolsRes?.data?.industry_groups || [];

        setAssessmentTemplate({
          stages: Array.isArray(nextAssessment.stages) ? nextAssessment.stages : [],
          opening_guidance: Array.isArray(nextAssessment.opening_guidance) ? nextAssessment.opening_guidance : [],
          main_objectives: Array.isArray(nextAssessment.main_objectives) ? nextAssessment.main_objectives : []
        });
        setToolCatalog(Array.isArray(nextCatalog) ? nextCatalog : []);
        setToolIndustryGroups(Array.isArray(nextIndustryGroups) ? nextIndustryGroups : []);
        setMetaError("");
      } catch (error) {
        if (!cancelled) {
          setAssessmentTemplate(EMPTY_ASSESSMENT_TEMPLATE);
          setToolCatalog([]);
          setToolIndustryGroups([]);
          setMetaError(
            error?.response?.data?.message
              || "Metadata API unavailable. Verify backend is restarted and DB migration update_2026_04_12_metadata_and_notes.sql is applied."
          );
        }
      } finally {
        if (!cancelled) {
          setMetaLoading(false);
        }
      }
    };

    loadMeta();
    return () => {
      cancelled = true;
    };
  }, []);

  const toolBySlugMap = useMemo(() => {
    return toolCatalog.reduce((acc, tool) => {
      if (tool?.slug) {
        acc[String(tool.slug)] = tool;
      }
      return acc;
    }, {});
  }, [toolCatalog]);

  const value = useMemo(() => ({
    assessmentTemplate,
    toolCatalog,
    toolIndustryGroups,
    metaLoading,
    metaError,
    getToolBySlug: (toolSlug = "") => toolBySlugMap[String(toolSlug)] || null
  }), [assessmentTemplate, toolCatalog, toolIndustryGroups, metaLoading, metaError, toolBySlugMap]);

  return <MetaContext.Provider value={value}>{children}</MetaContext.Provider>;
}

export function useMeta() {
  return useContext(MetaContext);
}
